/**
 * Parses a date string into a Date object, ensuring proper ISO format compatibility across browsers.
 * If the input string doesn't include a time component ('T'), adds '00:00:00' as the time.
 * Handles dates with and without timezone information, defaulting to UTC if no timezone specified.
 *
 * @param dateString - The date string to parse (e.g. "2024-03-14", "2024-03-14T15:30:00", or "2024-03-14T15:30:00+01:00")
 * @returns A Date object representing the parsed date
 */
export function parseDateString(dateString: string): Date {
    // remove milliseconds
    dateString = dateString.replace(/\.\d+/, '');

    // Check if string matches ISO format with optional time and timezone
    const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:?\d{2})?)?$/;
    if (!isoRegex.test(dateString)) {
        console.error(`Invalid date format: ${dateString}. Expected ISO format YYYY-MM-DD[THH:mm:ss[Z|±HH:mm]]`);
        return new Date(NaN); // Return invalid date
    }

    // If string already contains timezone info (Z, +, or -), parse directly
    if (/(Z|[+-]\d{2}:?\d{2})$/.test(dateString)) {
        return new Date(dateString);
    }

    // Otherwise format as UTC
    const formattedDate = dateString.includes('T') ? dateString : `${dateString}T00:00:00`;
    return new Date(Date.parse(formattedDate + 'Z'));
}

/**
 * Converts a Date object to an ISO 8601 date string (YYYY-MM-DD) without time component.
 * Uses the browser's built-in toISOString() method and trims the time part.
 *
 * @param date - The Date object to convert to string
 * @returns A string in YYYY-MM-DD format representing the date in UTC
 */
export function stringifyDate(date: Date): string {
    // Use the built-in toISOString() method which returns in format YYYY-MM-DDTHH:mm:ss.sssZ
    // Then extract just the date portion (everything before the 'T')
    return date.toISOString().split('T')[0];
}
